import { ArrowRight, Network, BookOpen, Gamepad2, GraduationCap } from "lucide-react";
import { useTranslation } from "react-i18next";
import Seo from "@/components/Seo";
import Header from "./Header";
import Footer from "./Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const highlightIcons = [
  <Network className="h-5 w-5" />,
  <Gamepad2 className="h-5 w-5" />,
  <BookOpen className="h-5 w-5" />,
  <GraduationCap className="h-5 w-5" />,
];

export default function Products() {
  const { t } = useTranslation();
  const card = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const details = useScrollReveal<HTMLDivElement>();

  const highlights = t("products.highlights", { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Seo title={t("seo.products.title")} description={t("seo.products.desc")} path="/products" />
      <Header />
      <main id="main" tabIndex={-1} className="flex-1 outline-none">
        {/* Hero */}
        <section className="w-full px-6 pt-20 md:pt-28 pb-16 md:pb-24">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between ed-eyebrow text-muted-foreground animate-fade-in">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand ed-blink" />
                {t("products.eyebrow")}
              </span>
              <span>{t("products.side")}</span>
            </div>

            <h1 className="ed-display mt-10 max-w-[14ch] animate-fade-in">
              {t("products.title")}<span className="text-brand">.</span>
            </h1>

            <p className="ed-serif mt-8 text-2xl md:text-3xl max-w-2xl text-foreground/85 animate-fade-in-delay-1">
              {t("products.sub")}
            </p>
          </div>
        </section>

        <div className="ed-rule mx-6 max-w-[1400px] md:mx-auto" />

        {/* Linko single product, full width */}
        <section className="w-full py-20 md:py-28 px-6">
          <div
            ref={card.ref}
            className={`max-w-[1400px] mx-auto reveal reveal-up ${card.isVisible ? "visible" : ""}`}
          >
            <article>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr] gap-12 md:gap-20">
                {/* Left info */}
                <div className="flex flex-col">
                  <div className="ed-eyebrow text-muted-foreground">{t("products.linkoLabel")}</div>
                  <h2 className="ed-headline mt-4 uppercase">Linko</h2>
                  <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-md">
                    {t("products.linkoDesc")}
                  </p>

                  <div className="mt-10 flex flex-wrap gap-3">
                    <a href="/contact" className="ed-btn ed-btn-solid">
                      {t("products.joinWaitlist")}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                    <a href="/contact" className="ed-btn">
                      {t("products.requestDemo")}
                    </a>
                  </div>
                </div>

                {/* Right highlights as editorial numbered grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-border">
                  {highlights.map((h, i) => (
                    <div
                      key={i}
                      className="group flex flex-col gap-3 border-b border-border py-7 sm:py-8 sm:even:pl-7 sm:odd:pr-7 sm:odd:border-r"
                    >
                      <div className="flex items-baseline justify-between">
                        <span className="ed-eyebrow text-muted-foreground">
                          0{i + 1}
                        </span>
                        <span className="text-muted-foreground transition-colors duration-200 group-hover:text-brand">
                          {highlightIcons[i]}
                        </span>
                      </div>
                      <h3 className="ed-headline text-xl md:text-2xl">{h.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{h.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </section>

        <div className="ed-rule mx-6 max-w-[1400px] md:mx-auto" />

        {/* Why Linko */}
        <section className="w-full py-20 md:py-28 px-6">
          <div
            ref={details.ref}
            className={`max-w-[1400px] mx-auto reveal reveal-up ${details.isVisible ? "visible" : ""}`}
          >
            <div className="ed-eyebrow text-muted-foreground mb-12">{t("products.whyLinko")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="flex flex-col gap-4 md:pr-12 md:border-r md:border-border pb-10 md:pb-0">
                <div className="ed-serif text-2xl text-foreground/70">{t("products.philosophyLabel")}</div>
                <h3 className="ed-headline">{t("products.philTitle")}</h3>
                <p className="text-muted-foreground leading-relaxed max-w-md">
                  {t("products.philDesc")}
                </p>
              </div>
              <div className="flex flex-col gap-4 md:pl-12 pt-10 md:pt-0 border-t border-border md:border-t-0">
                <div className="ed-serif text-2xl text-foreground/70">{t("products.builtByLabel")}</div>
                <h3 className="ed-headline">{t("products.qualityTitle")}</h3>
                <p className="text-muted-foreground leading-relaxed max-w-md">
                  {t("products.qualityDesc")}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
